import React, { FC, useEffect, useRef, useState } from 'react';
import { IImageOverlayModel } from '../../models/ImageOverlayModel';
import { IOverlayDataValueModel } from '../../models/DataValueModel';
import { OverLayFields } from '../asset-status/models/OverlayFields';
import { useParams } from 'react-router-dom';
import { useAppSelector } from '../../hooks/storeHooks';
import { DisplayStatusStateCssClassMap } from '../asset-status/DisplayStatusStateCssClassMap';
import RefreshIcon from '../asset-status/icons/RefreshIcon';
import { DiagramTypes } from '../asset-status/models/DiagramTypes';
import Loader from '../common/Loader/Loader';
import { GetDataOverlayCssClass } from './Utils';
import './AssetImageOverlay.css';
import { formatDate } from '../../utilities/dateHelpers';

interface Props {
  bkgndImgUrl: string;
  data: IImageOverlayModel[];
  diagramType: number;
  containerRef: React.RefObject<HTMLDivElement>;
  onRefresh: () => void;
}

export const AssetImageOverlay: FC<Props> = ({ bkgndImgUrl, data, diagramType, containerRef, onRefresh }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [hasCompletedFirstTimeScale, setHasCompletedFirstTimeScale] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const overlayLayerRef = useRef<HTMLDivElement>(null);
  const isLoading = useAppSelector((state) => state.rodliftwellstatus.loading);
  const [scaleFactor, setScaleFactor] = useState(1);

  const { assetId } = useParams<{ assetId: string }>();

  const resizeObserver = new ResizeObserver((entries) => {
    window.requestAnimationFrame(() => {
      if (!Array.isArray(entries) || !entries.length) {
        return;
      }
      ScaleImageToFit();
    });
  });

  useEffect(() => {
    setImageLoaded(false);
    setHasCompletedFirstTimeScale(false);
  }, [assetId]);

  useEffect(() => {
    if (imageLoaded && !hasCompletedFirstTimeScale && data) {
      ScaleImageToFit();
      setHasCompletedFirstTimeScale(true);
    }
  }, [imageLoaded, hasCompletedFirstTimeScale, data]);

  useEffect(() => {
    if (!containerRef.current) return;

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [containerRef]);

  // Helper to get the portal target (container div)
  function ScaleImageToFit() {
    if (!imageRef.current || !overlayLayerRef.current) return;

    const padding = 14;

    const scaleFactor = imageRef.current.clientWidth / imageRef.current.naturalWidth;
    setScaleFactor(scaleFactor);

    overlayLayerRef.current.style.transform = `scale(${scaleFactor})`;
    overlayLayerRef.current.style.transformOrigin = 'top left';
    overlayLayerRef.current.style.width = `${imageRef.current.naturalWidth}px`;
    overlayLayerRef.current.style.height = `${imageRef.current.naturalHeight}px`;

    overlayLayerRef.current.style.position = 'absolute';
    overlayLayerRef.current.style.top = `${padding}px`;
    overlayLayerRef.current.style.left = `${padding}px`;
  }

  const handleImageLoaded = () => setImageLoaded(true);

  const handleRefresh = () => {
    onRefresh();
    setHasCompletedFirstTimeScale(false);
    setImageLoaded(false);
  };

  const shouldUseLargeRunStatus =
    diagramType !== DiagramTypes.TundraSSi &&
    diagramType !== DiagramTypes.NOVCoreLiftController &&
    diagramType !== DiagramTypes.LowProfile &&
    diagramType !== DiagramTypes.UnicoLRP &&
    diagramType !== DiagramTypes.HydraulicStrokingUnit;

  // Track collapsed state for overlays by index
  const [collapsedOverlays, setCollapsedOverlays] = useState<{ [key: number]: boolean }>({});

  // Set all overlays to collapsed (true) initially, whenever data changes
  useEffect(() => {
    if (data && Array.isArray(data)) {
      const initialCollapsed: { [key: number]: boolean } = {};
      data.forEach((_, idx) => {
        initialCollapsed[idx] = true;
      });
      setCollapsedOverlays(initialCollapsed);
    }
  }, [data]);

  const [isSmallScreen, setIsSmallScreen] = useState(false);

  useEffect(() => {
    // Function to check screen size (max-width: 1024px for tablets and below)
    const checkScreenSize = () => {
      setIsSmallScreen(window.innerWidth <= 1279);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const toggleOverlayCollapse = (index: number) => {
    setCollapsedOverlays((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  // Consolidate ControllerType and RTUAddress into a single overlay item
  const controllerTypeDatum = data?.find(
    (iom) => iom.dataValues.length > 0 && iom.dataValues[0]?.overlayField === OverLayFields.ControllerType,
  );
  const rtuAddressDatum = data?.find(
    (iom) => iom.dataValues.length > 0 && iom.dataValues[0]?.overlayField === OverLayFields.RTUAddress,
  );
  const timeInStateDatum = data?.find(
    (iom) => iom.dataValues.length > 0 && iom.dataValues[0]?.overlayField === OverLayFields.TimeInState,
  );

  return (
    <div
      ref={containerRef}
      className='flex card asset-status-card flex-column'
      style={{ width: '100%', alignItems: 'flex-start', justifyContent: 'flex-start' }}
    >
      <div ref={headerRef} className='w-full header'>
        <div className='flex flex-row justify-between w-full'>
          <span className='title'>Well status</span>
          <div analytics-id='asset-status-refresh-btn' className='asset-status-refresh-icon' onClick={handleRefresh}>
            <RefreshIcon />
          </div>
        </div>
      </div>
      <div
        className='flex image-view-component body p-relative'
        style={{
          width: '100%',
          height: '100%',
          alignItems: 'flex-start',
        }}
      >
        {isLoading && (
          <div
            style={{
              display: 'flex',
              width: '100%',
              height: '100%',
            }}
          >
            <Loader />
          </div>
        )}
        {!isLoading && (
          <img
            style={{ marginTop: 60 * scaleFactor }}
            ref={imageRef}
            src={bkgndImgUrl}
            alt='Well Diagram'
            onLoad={handleImageLoaded}
          />
        )}
        {!isLoading && imageLoaded ? (
          <div
            ref={overlayLayerRef}
            className='data-overlay-layer'
            style={{
              width: '100%',
              height: '100%',
              position: 'absolute',
              top: 0,
              left: 0,
              zIndex: 2,
              pointerEvents: 'auto',
            }}
          >
            {/* Plot the container for API Designation and Last Good Scan first. */}
            {data && (
              <div
                className='data-overlay api-designation mt-0'
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '12px',
                  alignItems: 'center',
                }}
              >
                {data
                  .filter(
                    (iom) =>
                      iom.dataValues.length > 0 &&
                      (iom.dataValues[0]?.overlayField === OverLayFields.ApiDesignation ||
                        iom.dataValues[0]?.overlayField === OverLayFields.LastGoodScan ||
                        iom.dataValues[0]?.overlayField === OverLayFields.LastCardTime ||
                        iom.dataValues[0]?.overlayField === OverLayFields.LastSetPointScanTime ||
                        iom.dataValues[0]?.overlayField === OverLayFields.APIDesignation),
                  )
                  ?.map((datum) =>
                    datum.dataValues?.map((dv: IOverlayDataValueModel, index: number) => {
                      const isDateField =
                        dv.overlayField === OverLayFields.LastGoodScan ||
                        dv.overlayField === OverLayFields.LastCardTime ||
                        dv.overlayField === OverLayFields.LastSetPointScanTime;
                      return (
                        <div
                          key={index}
                          className={`data-overlay-item${dv.overlayField === OverLayFields.LastGoodScan ? ' lastgoodscan-overlay' : ''}`}
                          title={`${dv.label}: ${dv.value}`}
                          style={{
                            borderRadius: '4px',
                          }}
                        >
                          <span className='label'>{dv.label}:</span>
                          <span className='value'>
                            {isDateField ? formatDate(dv.value, true) : dv.value ?? 'No data'}
                          </span>
                        </div>
                      );
                    }),
                  )}
              </div>
            )}
            {/* Plot the general case */}
            {controllerTypeDatum && (
              <div
                className='data-overlay asset-status-data-overlay'
                style={{
                  left: controllerTypeDatum?.position?.x ? `${controllerTypeDatum.position.x}px` : '0px',
                  top: controllerTypeDatum?.position?.y ? `${controllerTypeDatum.position.y}px` : '50px',
                  position: 'absolute',
                }}
              >
                <div>
                  <span className='common-asset-label'>Controller:</span>
                  <span className='common-asset-value'> {controllerTypeDatum.dataValues[0].value}</span>
                </div>
              </div>
            )}
            {rtuAddressDatum && (
              <div
                className='data-overlay asset-status-data-overlay'
                style={{
                  left: rtuAddressDatum?.position?.x ? `${rtuAddressDatum.position.x}px` : '0px',
                  top: rtuAddressDatum?.position?.y ? `${rtuAddressDatum.position.y}px` : '50px',
                  position: 'absolute',
                }}
              >
                <div>
                  <span className='common-asset-label'>Address:</span>
                  <span className='common-asset-value'> {rtuAddressDatum.dataValues[0].value}</span>
                </div>
              </div>
            )}
            {timeInStateDatum && (
              <div
                className='data-overlay asset-status-data-overlay'
                style={{
                  left: timeInStateDatum?.position?.x ? `${timeInStateDatum.position.x}px` : '0px',
                  top: timeInStateDatum?.position?.y ? `${timeInStateDatum.position.y}px` : '50px',
                  position: 'absolute',
                }}
              >
                <div>
                  <span className='common-asset-label'>TIS:</span>
                  <span className='common-asset-value'> {timeInStateDatum.dataValues[0].value}</span>
                </div>
              </div>
            )}
            {data?.map((iom: IImageOverlayModel, ndx: number) => {
              if (
                (iom.dataValues.length > 0 && iom.dataValues[0]?.overlayField === OverLayFields.ApiDesignation) ||
                iom.dataValues[0]?.overlayField === OverLayFields.LastGoodScan ||
                iom.dataValues[0]?.overlayField === OverLayFields.ControllerType ||
                iom.dataValues[0]?.overlayField === OverLayFields.RTUAddress ||
                iom.dataValues[0]?.overlayField === OverLayFields.TimeInState ||
                iom.dataValues[0]?.overlayField === OverLayFields.LastCardTime ||
                iom.dataValues[0]?.overlayField === OverLayFields.LastSetPointScanTime ||
                iom.dataValues[0]?.overlayField === OverLayFields.APIDesignation
              ) {
                return;
              }

              const isCollapsible = isSmallScreen && iom.dataValues.length > 1;
              const isCollapsed = collapsedOverlays[ndx];

              return (
                <div>
                  {iom.title && isCollapsible && (
                    <>
                      <div
                        className='data-overlay collapsible-header'
                        onClick={() => toggleOverlayCollapse(ndx)}
                        style={{
                          left: iom.justification === 'right' ? 'unset' : `${iom.position.x}px`,
                          right: iom.justification === 'right' ? `${iom.position.x}px` : 'unset',
                          top: `${iom.position.y + 5}px`,
                          maxHeight: iom.maxHeight ?? 'unset',
                          cursor: isCollapsible ? 'pointer' : undefined,
                          borderRadius: '8px',
                        }}
                      >
                        <span
                          style={{
                            fontWeight: 'bold',
                            fontSize: 18,
                            color: 'black',
                            display: 'inline-block',
                            width: 16,
                            textAlign: 'center',
                            userSelect: 'none',
                          }}
                        >
                          {isCollapsed ? '+' : '-'}
                        </span>
                      </div>
                      {/* <div
                        className={
                          'data-overlay' +
                          (iom.dataValues.length > 1 ? ' asset-status-data-overlay' : '') +
                          (iom.dataValues.length === 1 ? ' asset-status-single-data-overlay' : '')
                        }
                        style={{
                          left: iom.justification === 'right' ? 'unset' : `${iom.position.x}px`,
                          right: iom.justification === 'right' ? `${iom.position?.x}px` : 'unset',
                          top: `${iom.position.y}px`,
                          maxHeight: iom.maxHeight ?? 'unset',
                          maxWidth: iom.maxWidth ?? 'unset',
                        }}
                      >
                        {!isCollapsed ? <span>{iom.title} </span> : ''}
                      </div> */}
                    </>
                  )}
                  <div
                    key={ndx}
                    className={
                      (iom.dataValues.length === 1
                        ? ` asset-status-single-data-overlay ${iom.overlayField === OverLayFields.CurrentCommunicationStatus ? ` ${DisplayStatusStateCssClassMap.get(iom.dataValues[0].displayState)}-code` : ''}`
                        : '') +
                      ' data-overlay' +
                      (iom.dataValues.length > 1 ? ' asset-status-data-overlay' : '')
                    }
                    style={{
                      left:
                        iom.justification === 'right'
                          ? 'unset'
                          : isCollapsible && !isCollapsed
                            ? `${iom.smallPosition?.x}px`
                            : `${iom.position.x}px`,
                      right:
                        iom.justification === 'right'
                          ? `${isCollapsible ? iom.position?.x + 35 : iom.position?.x}px`
                          : 'unset',
                      top: isCollapsible && !isCollapsed ? `${iom.smallPosition?.y}px` : `${iom.position.y}px`,
                      maxHeight: isCollapsed && iom.maxHeight ? iom.maxHeight : 'unset',
                      maxWidth: isCollapsed && iom.maxWidth ? iom.maxWidth : 'unset',
                      minWidth: isCollapsible && !isCollapsed && iom.minWidth ? iom.minWidth : 'unset',
                      borderRadius: '8px',
                    }}
                  >
                    {iom.title && (
                      <div
                        className={`${!isCollapsed ? 'mb-3 data-overlay-title-expanded' : ''}`}
                        style={{
                          padding: iom.dataValues.length > 0 ? '0px' : '8px',
                          userSelect: 'none',
                          position: 'relative',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        {iom.title}
                        {!isCollapsible && ':'}
                        {isCollapsible && !isCollapsed && (
                          <span
                            style={{
                              position: 'absolute',
                              right: 0,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              fontWeight: 'bold',
                              fontSize: 20,
                              color: 'white',
                              cursor: 'pointer',
                              padding: '0 8px',
                              userSelect: 'none',
                            }}
                            title='Collapse'
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleOverlayCollapse(ndx);
                            }}
                          >
                            ×
                          </span>
                        )}
                      </div>
                    )}
                    {(!isCollapsible || !isCollapsed) && (
                      <div className='flex flex-col w-full gap-2'>
                        {iom.dataValues?.map((dv: IOverlayDataValueModel, index: number) => {
                          const showValue =
                            dv.overlayField !== OverLayFields.ControllerInformation &&
                            dv.overlayField !== OverLayFields.ControllerAndNode &&
                            dv.overlayField !== OverLayFields.IntermittenRealTime &&
                            dv.overlayField !== OverLayFields.FirstHead &&
                            dv.overlayField !== OverLayFields.SecondHead;

                          const shouldUseNormalDisplayState =
                            dv.overlayField === OverLayFields.CurrentCommunicationStatus ||
                            (dv.overlayField === OverLayFields.RunStatus && !shouldUseLargeRunStatus);

                          const breakLine = dv.overlayField === OverLayFields.ControllerAndNode;
                          const shouldWrap =
                            dv.overlayField === OverLayFields.CurrentLoadFactor &&
                            diagramType === DiagramTypes.MultiWellPlungerLift;

                          return (
                            <div
                              key={index}
                              className={
                                GetDataOverlayCssClass(dv, shouldUseNormalDisplayState) +
                                (iom.dataValues.length > 1 ? ' gap-3' : '')
                              }
                              title={`${dv.label}: ${dv.value}`}
                            >
                              {shouldUseNormalDisplayState && (
                                <div className='indicator'>
                                  <div
                                    className={`status-indicator ${DisplayStatusStateCssClassMap.get(dv.displayState)}`}
                                  />
                                </div>
                              )}
                              <span className='label'>
                                {breakLine
                                  ? dv.label.split('\n').map((word, index) => <div key={index}>{word}</div>)
                                  : dv.label}
                                {showValue && dv.label != '' ? ':' : null}
                              </span>
                              {showValue && (
                                <span
                                  style={{
                                    whiteSpace: shouldWrap || iom.maxWidth ? 'normal' : 'nowrap',
                                    maxWidth: iom.maxWidth ?? 'unset',
                                  }}
                                  className='value common-asset-value'
                                  data-overlay
                                >
                                  {dv.value && dv.value !== '' ? dv.value : 'No data'}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
};
